import React, { useState, useEffect, useRef } from "react";
import * as Constants from "../constants";
import JisButton from "./controls/jisButton"; // Adjust path to match your controls folder structure

function JisVoiceRecorder({ transcribeUrl, token, onTranscriptionComplete, onStatusChange }) {
  const [isListening, setIsListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Core Audio References
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  // VAD & Silence Management References
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const isCyclingRef = useRef(false);

  // Clean up recording tracks safely if the user changes pages mid-stream
  useEffect(() => {
    return () => stopAudioPipeline();
  }, []);

  const toggleVoiceRecording = async () => {
    if (isListening) {
      stopAudioPipeline();
      onStatusChange && onStatusChange(false);
    } else {
      await startAudioPipeline();
      onStatusChange && onStatusChange(true);
    }
  };

  const startAudioPipeline = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setIsListening(true);

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      startRecorderInstance();
      monitorSilence();
    } catch (err) {
      console.error("Microphone initialization failure:", err);
      setErr("Could not open microphone.");
    }
  };

  const startRecorderInstance = () => {
    if (!streamRef.current) return;

    audioChunksRef.current = [];
    const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType: "audio/webm;codecs=opus" });
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = async (event) => {
      if (event.data && event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm;codecs=opus" });
      
      // Filter out micro background room clicks under 2KB
      if (audioBlob.size > 2000) {
        await handleTranscribeAudio(audioBlob);
      }

      if (isCyclingRef.current) {
        isCyclingRef.current = false;
        startRecorderInstance();
      }
    };

    mediaRecorder.start();
  };

  const monitorSilence = () => {
    if (!analyserRef.current) return;

    const bufferLength = analyserRef.current.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    
    const checkVolume = () => {
      if (!analyserRef.current || !streamRef.current) return;
      
      analyserRef.current.getByteTimeDomainData(dataArray);
      
      let sumSquares = 0.0;
      for (let i = 0; i < bufferLength; i++) {
        const norm = (dataArray[i] - 128) / 128;
        sumSquares += norm * norm;
      }
      const rms = Math.sqrt(sumSquares / bufferLength);

      if (rms < 0.015) {
        if (!silenceTimeoutRef.current) {
          silenceTimeoutRef.current = setTimeout(() => {
            cycleAudioChunk();
          }, 1500); // 1.5 seconds pause detection configuration
        }
      } else {
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
      }

      if (streamRef.current && streamRef.current.active) {
        requestAnimationFrame(checkVolume);
      }
    };

    requestAnimationFrame(checkVolume);
  };

  const cycleAudioChunk = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      isCyclingRef.current = true;
      mediaRecorderRef.current.stop();

      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
    }
  };

  const stopAudioPipeline = () => {
    setIsListening(false);
    isCyclingRef.current = false;
    
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }
  };

  const handleTranscribeAudio = async (audioBlob) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("file", audioBlob, "recording.webm");

    try {
      const response = await fetch(transcribeUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      setLoading(false);

      if (data.text && data.text.trim().length > 0) {
        // 🚀 Bubble the clean string back out to the parent view function container hook
        if (typeof onTranscriptionComplete === "function") {
          onTranscriptionComplete(data.text.trim());
        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Transcription pipeline chunk delivery failed:", error);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <JisButton variant={isListening ? "danger" : "outline"} onClick={toggleVoiceRecording}>
        {isListening ? "Stop Voice Engine" : "Start Voice Engine"}
      </JisButton>
      {loading && <span style={{ color: "#007bff", fontSize: "14px" }}>Processing speech...</span>}
      {err && <p style={{ color: "red", margin: 0, fontSize: "14px" }}>{err}</p>}
    </div>
  );
}

export default JisVoiceRecorder;